// Main container styles
export const mainContainer = {
  backgroundColor: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0, // when nested in a flex parent, allow column to shrink so only contentContainer scrolls
  overflow: 'hidden', // Prevent main container scroll; content area handles scrolling
};

export const SnackBarComponent = {
  position: 'fixed',
  left: '32%',
  bottom: 0,
  zIndex: 99,
};

// Header styles
export const headerContainer = {
  display: 'flex',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #E5E7EB',
  backgroundColor: 'white',
};

export const closeButton = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  border: '1px solid #0F48B8',
  '&:hover': {
    backgroundColor: '#F3F4F6',
  },
};

export const headerTitle = {
  color: 'var(--Navy---navy-main, #061E44)',
  leadingTrim: 'both',
  textEdge: 'cap',
  fontFamily: 'Inter',
  fontSize: '32px',
  fontStyle: 'normal',
  fontWeight: 800,
  lineHeight: 'normal',
};

export const headerTextContainer = {
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '12px',
};

// Content area styles
export const contentContainer = {
  padding: '24px 20px 24px 20px',
  backgroundColor: 'white',
  flex: 1,
  minHeight: 0, // flex child: keep scroll inside this box instead of growing layout + page scroll
  overflowY: 'auto',
  overflowX: 'hidden',
};

/** Permissions step — Figma: 24px horizontal padding (align with spec when parent uses 20px on details) */
export const contentContainerPermissions = {
  padding: '24px 24px 16px 24px',
  backgroundColor: 'white',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
};

// Form container styles
export const formContainer = {
  width: '100%',
  maxWidth: '100%',
};

// Text fields: flex rows keep columns adjacent (avoids huge gap from xs={6} + maxWidth fields)
export const textFieldsLayout = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

export const textFieldRow = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  columnGap: '16px',
  rowGap: '16px',
};

// Form group styles
export const formGroup = {
  marginBottom: '24px',
};

// Form field styles - wrapper for TextInput component
export const formField = {
  display: 'flex',
  width: '100%',
  maxWidth: '409px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flexShrink: 0,
  // Label styles (always shrunken/visible above input)
  '& .MuiInputLabel-root': {
    color: 'var(--primary-blue-primary-deep, #0F2C54)',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '22px',
    letterSpacing: '0.28px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--primary-blue-primary-deep, #0F2C54)',
  },
  // Input text styles
  '& .MuiInputBase-input': {
    color: 'var(--Greyscale---grey-110, #333)',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.32px',
  },
};

// Field cell: fixed max width so pairs sit side-by-side with only columnGap between them
export const textFieldCell = {
  flex: '0 1 409px',
  maxWidth: '409px',
  minWidth: 0,
  width: '100%',
};

// Field spacing for left column
export const fieldSpaceLeft = {
  marginLeft: 0,
};

// Footer styles
export const footerContainer = {
  display: 'flex',
  minHeight: '65px',
  padding: '0 24px',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
  alignSelf: 'stretch',
  borderTop: '1px solid var(--Greyscale---grey-60, #CDD4E3)',
  backgroundColor: 'white',
  // Removed sticky positioning to prevent hiding location chips
  // Footer will now be at the bottom of the content, scrollable with the form
};

export const createButton = {
  borderRadius: '100px',
  background: 'var(--primary-blue-primary-main, #0F48B8)',
  display: 'flex',
  minHeight: '40px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  color: 'white',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '0 24px',
  boxShadow: 'none',
  '&:hover': {
    background: 'var(--primary-blue-primary-main, #0F48B8)',
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: '#9CA3AF',
    boxShadow: 'none',
  },
};

/**
 * Returns button styles based on disabled state
 * @param {boolean} isDisabled - Whether the button is disabled
 * @returns {Object} - Button style object
 */
export const getCreateButtonStyles = (isDisabled) => {
  if (isDisabled) {
    return {
      ...createButton,
      backgroundColor: '#9CA3AF',
      background: '#9CA3AF',
      color: '#FFFFFF',
      cursor: 'not-allowed',
    };
  }

  return {
    ...createButton,
    backgroundColor: 'var(--primary-blue-primary-main, #0F48B8)',
    background: 'var(--primary-blue-primary-main, #0F48B8)',
    color: '#FFFFFF',
  };
};

export const cancelButton = {
  display: 'flex',
  height: '40px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '100px',
  border: '1px solid var(--Navy---navy-main, #061E44)',
  backgroundColor: 'white',
  color: '#374151',
  padding: '0 24px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: 'white',
    border: '1px solid var(--Navy---navy-main, #061E44)',
  },
};

// Role section styles
export const roleSectionHeading = {
  color: 'var(--Cerulean---cerulean-main, #0096CA)',
  fontFamily: 'Rubik',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: '1.8px',
  textTransform: 'uppercase',
  marginBottom: '16px',
  marginTop: '24px',
};

export const roleCardsContainer = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(412px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
};

// Role card styles
export const roleCard = {
  display: 'flex',
  width: '412px',
  height: '72px',
  padding: '16px',
  alignItems: 'center',
  gap: '12px',
  borderRadius: '10px',
  border: '0.5px solid var(--primary-blue-primary-greyed, #C9D2DF)',
  background: 'var(--Greyscale-white, #FFF)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F9FAFB',
  },
};

export const roleCardSelected = {
  ...roleCard,
  border: '2px solid var(--primary-blue-primary-main, #0F48B8)',
  backgroundColor: '#F0F4FF',
};

export const roleCardIcon = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const roleCardText = {
  flex: 1,
  color: 'var(--primary-blue-primary-deep, #0F2C54)',
  textAlign: 'justify',
  fontFamily: 'Inter',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '24px',
};

export const roleCardRadio = {
  flexShrink: 0,
};

// Location section styles
export const locationSectionHeading = {
  color: 'var(--Cerulean---cerulean-main, #0096CA)',
  leadingTrim: 'both',
  textEdge: 'cap',
  fontFamily: 'Rubik',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: '1.8px',
  textTransform: 'uppercase',
  marginBottom: '8px',
};

export const locationSectionDescription = {
  color: '#000',
  leadingTrim: 'both',
  textEdge: 'cap',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px', /* 150% */
  letterSpacing: '0.32px',
  marginBottom: '24px',
};

export const inlineFieldError = {
  color: '#f44336',
  fontSize: '12px',
  fontFamily: 'Inter',
};

export const roleSectionWrapper = { marginTop: '32px' };

export const roleErrorText = {
  ...inlineFieldError,
  marginTop: '-16px',
  marginBottom: '8px',
};

export const locationFieldError = {
  ...inlineFieldError,
  marginTop: '4px',
};

export const facilitySectionBox = {
  marginTop: '32px',
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
};

export const facilityAutocompleteBox = { marginBottom: '24px' };

/** Optional permission step — Figma: column, gap 32px, bottom padding 36px before footer */
export const permissionStepContainer = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '32px',
  width: '100%',
  boxSizing: 'border-box',
  paddingBottom: '36px',
};

/** Figma node 1771:30586 — primary pale fill, primary border, 12px radius, 8×16 padding */
export const permissionInfoBanner = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 16px',
  borderRadius: '12px',
  border: '1px solid var(--primary-blue-primary-main, #0F48B8)',
  backgroundColor: 'var(--primary-blue-primary-pale, #F0F6FF)',
};

export const permissionInfoBannerText = {
  color: 'var(--Greyscale---grey-110, #333333)',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.32px',
  margin: 0,
};

/** Small grey uppercase group label for the permission catalog section */
export const permissionGroupSectionLabel = {
  color: 'var(--Cerulean---cerulean-main, #0096CA)',
  fontFamily: 'Rubik',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: '1.4px',
  textTransform: 'uppercase',
  marginBottom: '16px',
};

export const permissionGroupBlock = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
};

export const permissionStepDescription = {
  color: '#374151',
  fontFamily: 'Inter',
  fontSize: '16px',
  lineHeight: '24px',
  marginTop: '8px',
  marginBottom: '8px',
};

/** Figma node 29:6768 — list row: pt 12 pb 8, 16px gap to trailing toggle */
export const permissionToggleRow = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  width: '100%',
  boxSizing: 'border-box',
  paddingTop: '12px',
  paddingBottom: '8px',
  minHeight: '72px',
};

/** Left column: 12px vertical gap between title and supporting line */
export const permissionToggleTextColumn = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '12px',
  flex: 1,
  minWidth: 0,
  paddingRight: '8px',
};

/** Orijin/body/large bold */
export const permissionToggleLabel = {
  color: 'var(--Greyscale---grey-110, #333333)',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.32px',
};

/** Orijin/body/medium, italic supporting text */
export const permissionToggleDescription = {
  color: 'var(--Greyscale---grey-110, #333333)',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'italic',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.28px',
  maxWidth: '900px',
};

export const permissionStepErrorText = {
  color: '#d32f2f',
  fontFamily: 'Inter',
  fontSize: '14px',
  marginTop: '16px',
};

/** Staff profile Permissions tab — Figma 359:22272 header row */
export const permissionsProfileTabHeader = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 24px 32px 24px',
  borderBottom: '1px solid var(--Greyscale---grey-60, #CDD4E3)',
};

export const permissionsProfileTabTitle = {
  color: 'var(--Greyscale---grey-110, #333333)',
  fontFamily: 'Inter',
  fontSize: '24px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
  margin: 0,
  marginBottom: '12px',
};

export const permissionsProfileTabIntro = {
  color: '#000000',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.32px',
  margin: 0,
};

/** Outlined pill — Edit Permissions (Figma primary outline, 40px height target) */
export const permissionsProfileEditButton = {
  borderRadius: '100px',
  border: '1px solid var(--primary-blue-primary-main, #0F48B8)',
  color: 'var(--primary-blue-primary-main, #0F48B8)',
  backgroundColor: '#FFFFFF',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontWeight: 700,
  letterSpacing: '0.32px',
  textTransform: 'none',
  padding: '10px 20px',
  minHeight: '40px',
  minWidth: '200px',
  boxShadow: 'none',
};

/** Figma 359:23015 — body below header (view or edit) */
export const permissionsProfileTabBody = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
  boxSizing: 'border-box',
  padding: '24px 24px 36px 24px',
  gap: '32px',
};

/** Profile Permissions tab — group overline (Figma Rubik cerulean uppercase) */
export const permissionsProfileGroupOverline = {
  ...locationSectionHeading,
  marginBottom: '12px',
};

/** ON / OFF status chip — Figma input chip solid, 4px radius; fixed width so ON/OFF don’t shift layout */
export const permissionStatusChipBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: '60px',
  minWidth: '60px',
  padding: '6px 8px',
  borderRadius: '4px',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 700,
  letterSpacing: '0.32px',
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  textAlign: 'center',
};

export const permissionStatusChipOn = {
  ...permissionStatusChipBase,
  border: '1px solid var(--success-green-deep, #1A5E3D)',
  color: 'var(--success-green-deep, #1A5E3D)',
  backgroundColor: '#FFFFFF',
};

export const permissionStatusChipOff = {
  ...permissionStatusChipBase,
  border: '1px solid var(--error-red-dark, #9E162F)',
  color: 'var(--error-red-dark, #9E162F)',
  backgroundColor: '#FFFFFF',
};

/** Aliases for shared staff permission row styles (profile + create) */
export const staffPermissionDescription = permissionStepDescription;
export const staffPermissionRow = permissionToggleRow;
export const staffPermissionLabel = permissionToggleLabel;
export const staffPermissionErrorText = permissionStepErrorText;

export const selectedMembersBox = {
  position: 'relative',
  zIndex: 2,
  marginTop: '16px',
};


/** Mint-tint chips for Create Staff facility row — overrides `SelectedMembersSection` defaults via descendant `sx`. */
export const facilitySelectedMembersWrapSx = {
  '& > div': {
    marginTop: '0 !important',
    marginLeft: '0 !important',
  },
  '& .MuiChip-root': {
    backgroundColor: 'rgba(34, 128, 83, 0.12) !important',
    borderRadius: '6px',
    minHeight: '32px',
  },
  '& .MuiChip-label': {
    color: 'var(--primary-blue-primary-deep, #0F2C54) !important',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '22px',
    letterSpacing: '0.28px',
  },
  '& .MuiChip-deleteIcon': {
    color: 'var(--primary-blue-primary-deep, #0F2C54) !important',
  },
  '& .MuiChip-icon': {
    color: 'var(--success-green-green-dark, #228053) !important',
  },
};
